import { useState, useEffect } from 'react';
import { productService } from '../services/api';

// Hook pour obtenir tous les produits
export const useProducts = (params = {}) => {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await productService.getProducts(params);

        if (response.success) {
          setProducts(response.data.data || []);
          setPagination({
            current_page: response.data.current_page,
            last_page: response.data.last_page,
            per_page: response.data.per_page,
            total: response.data.total,
          });
        }
      } catch (error) {
        setError(error.response?.data?.message || 'Erreur lors du chargement des produits');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, [JSON.stringify(params)]);

  return { products, pagination, isLoading, error };
};

// Hook pour obtenir un produit spécifique
export const useProduct = (id) => {
  const [product, setProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError(null);

        const response = await productService.getProduct(id);

        // La réponse peut être dans response.data ou directement dans response
        if (response.success) {
          setProduct(response.data?.product || response.data);
        } else if (response.data) {
          // Si la réponse n'a pas success mais a data
          setProduct(response.data.product || response.data);
        }
      } catch (error) {
        setError(error.response?.data?.message || 'Erreur lors du chargement du produit');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  return { product, isLoading, error };
};

// Hook pour les produits en vedette
export const useFeaturedProducts = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await productService.getFeaturedProducts();

        if (response.success) {
          setProducts(response.data.products || []);
        }
      } catch (error) {
        setError(error.response?.data?.message || 'Erreur lors du chargement des produits en vedette');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, []);

  return { products, isLoading, error };
};

// Hook pour les produits les mieux notés
export const useTopRatedProducts = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTopRatedProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await productService.getTopRatedProducts();

        if (response.success) {
          setProducts(response.data.products || []);
        }
      } catch (error) {
        setError(error.response?.data?.message || 'Erreur lors du chargement des produits les mieux notés');
      } finally {
        setIsLoading(false);
      }
    };

    fetchTopRatedProducts();
  }, []);

  return { products, isLoading, error };
};

// Hook pour les produits en promotion
export const useOnSaleProducts = () => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOnSaleProducts = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await productService.getOnSaleProducts();

        if (response.success) {
          setProducts(response.data.products || []);
        }
      } catch (error) {
        setError(error.response?.data?.message || 'Erreur lors du chargement des produits en promotion');
      } finally {
        setIsLoading(false);
      }
    };

    fetchOnSaleProducts();
  }, []);

  return { products, isLoading, error };
};

// Hook pour les produits similaires
export const useSimilarProducts = (id) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSimilarProducts = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError(null);

        const response = await productService.getSimilarProducts(id);

        if (response.success) {
          setProducts(response.data.products || []);
        }
      } catch (error) {
        setError(error.response?.data?.message || 'Erreur lors du chargement des produits similaires');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSimilarProducts();
  }, [id]);

  return { products, isLoading, error };
};

// Hook pour la recherche de produits
export const useProductSearch = (searchParams) => {
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const search = async (params) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await productService.searchProducts(params);

      if (response.success) {
        setProducts(response.data.data || []);
        setPagination({
          current_page: response.data.current_page,
          last_page: response.data.last_page,
          per_page: response.data.per_page,
          total: response.data.total,
        });
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Erreur lors de la recherche');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (searchParams && Object.keys(searchParams).length > 0) {
      search(searchParams);
    }
  }, [JSON.stringify(searchParams)]);

  return { products, pagination, isLoading, error, search };
};







